import * as Page from '../../components/content/page-header';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { useNavigate, useLocation } from 'react-router';
import { PaginatedTable } from '../../components/table/paginated-table';
import { MainContent } from '../../components/layout/content';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../components/dialog/dialog';
import { ActionColumn, CellRenderer, GearIconColumn } from '../../components/table/columns';
import { Query, usePagination } from '../../hooks/use-pagination';
import React, { useState } from 'react';
import { useDeleteMutation } from '../../hooks/use-mutations';
import { StaticTranslationKey } from './types/static-key';
import { RevertToMaster2 } from '../../components/revert-to-master/revert-to-master-2';
import { NotInSync } from '../../components/not-in-sync/not-in-sync';
import { SearchField, useSearch } from '../../hooks/use-search';
import tenantService from '../../services/tenant-service';

export const StaticTranslationKeys = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState<Query>({});
  const { data, isLoading } = usePagination('/static-keys', query);
  const deleteKey = useDeleteMutation();
  const { isSearching, searchData, setSearchQuery } = useSearch<StaticTranslationKey>({ path: '/static-keys/search' });

  const onDeleteConfirm = (): void => {
    deleteKey.mutate({ path: `/static-keys/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const columns = [
    ...GearIconColumn(),
    { accessor: 'key', Header: 'Translation Key', sort: true, Cell: CellRenderer },
    ActionColumn({
      onDelete: tenantService.isGoldMaster() ? (item: StaticTranslationKey): void => setPageState(Modes.DELETE, item) : undefined,
      onUpdate: (item: StaticTranslationKey): void => navigate(`/internationalization/static-translation-keys/${item.id as string}`)
    })
  ];

  return <>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>Static Translation Keys <NotInSync path="static-keys" /></Page.Title>
      <Page.SubTitle>Static keys are used for static translations of the UI elements within RAPIDS.</Page.SubTitle>
      <Page.Actions>
        <SearchField />
        <div>
          <RevertToMaster2 path="static-keys" />
          {tenantService.isGoldMaster() && (
            <Button.PrimarySmall onClick={(): void => navigate(`${pathname}/new`)}>
              <Icons.Add />
              Add Static Translation Key
            </Button.PrimarySmall>
          )}
        </div>
      </Page.Actions>
    </Page.Header>
    <MainContent>
      {isSearching
        ? <PaginatedTable isLoading={isLoading} columns={columns} data={searchData} onChange={setSearchQuery} />
        : <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
      }
    </MainContent>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Static Translation Key</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
